import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import supersub from 'remark-supersub';
import { unified } from 'unified';

import {
  calculateDimensionValues,
  calculateOverallRating,
  getTileColor,
  getUniqueAreas,
  groupByAreaAndDimension,
} from '../../../components/DealGPT/PhaseTwo/Rubric/rubricUtils';
import {
  ControlSlideData,
  ImpactContent,
  ProjectRunAnalysisQuestion,
  RubricCore,
  TextBlock,
} from '../../../Types/dealGPT';

type MarkdownNode = {
  type: string;
  children?: MarkdownNode[];
  value?: string;
  ordered?: boolean;
  depth?: number;
  checked?: boolean;
  url?: string;
};

export const parseMarkdownToTextBlocks = (markdown: string): TextBlock[] => {
  const lines = markdown.split('\n');
  let processedMarkdown = '';

  for (let line of lines) {
    if (line.trim().startsWith('•')) {
      line = line.replace(/^(\s*)(•)(\s+)/, '$1-$3');
    }
    processedMarkdown += line + '\n';
  }

  const tree = unified().use(remarkParse).use(remarkGfm).use(supersub).parse(processedMarkdown);
  const textBlocks: TextBlock[] = [];

  function processEmphasisNode(node: MarkdownNode): string {
    let text = '';
    if (node.children) {
      node.children.forEach((child: MarkdownNode) => {
        if (child.type === 'text') {
          text += child.value || '';
        }
      });
    }
    return text;
  }

  function processStrongNode(node: MarkdownNode): string {
    let text = '';
    if (node.children) {
      node.children.forEach((child: MarkdownNode) => {
        if (child.type === 'text') {
          text += child.value || '';
        }
      });
    }
    return text;
  }

  function processLinkNode(node: MarkdownNode): string {
    let text = '';
    if (node.children) {
      node.children.forEach((child: MarkdownNode) => {
        if (child.type === 'text') {
          text += child.value || '';
        } else if (child.type === 'strong') {
          text += processStrongNode(child);
        } else if (child.type === 'emphasis') {
          text += processEmphasisNode(child);
        }
      });
    }
    return text;
  }

  function processInlineCodeNode(node: MarkdownNode): string {
    return node.value || '';
  }

  function processParagraph(
    para: MarkdownNode,
    indentLevel: number,
    isBullet = false,
    isOrdered = false,
    listItemNumber?: number
  ): void {
    const textParts: {
      text: string;
      options: {
        bold?: boolean;
        bullet?: boolean;
        numbered?: boolean;
        listItemNumber?: number;
        emphasis?: boolean;
      };
    }[] = [];
    const prefix = isOrdered && listItemNumber !== undefined ? `${listItemNumber}. ` : '';
    let currentText = '';
    let currentOptions: {
      bullet: boolean;
      numbered: boolean;
      listItemNumber?: number;
      bold?: boolean;
      emphasis?: boolean;
    } = {
      bullet: isBullet,
      numbered: isOrdered,
      listItemNumber: isOrdered ? listItemNumber : undefined,
    };
    let isFirstElement = true;

    const addTextPart = (text: string, options: { bold?: boolean; emphasis?: boolean }) => {
      if (text) {
        // If we're in a bullet point, add to current text without bold
        if (isBullet) {
          currentText += text;
          // Only keep emphasis formatting for bullet points
          if (options.emphasis) currentOptions = { ...currentOptions, emphasis: true };
        } else {
          // For non-bullet text, keep the original behavior with all formatting
          textParts.push({
            text,
            options: {
              bullet: false,
              numbered: false,
              ...options,
            },
          });
        }
      }
    };

    para.children?.forEach((child: MarkdownNode) => {
      if (child.type === 'text') {
        let text = child.value || '';
        if (isFirstElement && prefix) {
          text = prefix + text;
          isFirstElement = false;
        }
        addTextPart(text, {});
      } else if (child.type === 'strong') {
        let strongText = processStrongNode(child);
        if (isFirstElement && prefix) {
          strongText = prefix + strongText;
          isFirstElement = false;
        }
        addTextPart(strongText, { bold: true });
      } else if (child.type === 'emphasis') {
        let emphasisText = processEmphasisNode(child);
        if (isFirstElement && prefix) {
          emphasisText = prefix + emphasisText;
          isFirstElement = false;
        }
        addTextPart(emphasisText, { emphasis: true });
      } else if (child.type === 'link') {
        let linkText = processLinkNode(child);
        if (isFirstElement && prefix) {
          linkText = prefix + linkText;
          isFirstElement = false;
        }
        addTextPart(linkText, {});
      } else if (child.type === 'inlineCode') {
        let codeText = processInlineCodeNode(child);
        if (isFirstElement && prefix) {
          codeText = prefix + codeText;
          isFirstElement = false;
        }
        addTextPart(codeText, {});
      }
    });

    // If we were processing a bullet point, add the combined text as a single part
    if (isBullet && currentText) {
      textParts.push({
        text: currentText,
        options: { ...currentOptions },
      });
    }

    // Handle special case for bold text followed by colon
    if (
      textParts.length >= 2 &&
      textParts[0].options.bold &&
      textParts[0].text.endsWith(':') &&
      !textParts[1].options.bold
    ) {
      textParts[1].options = {
        ...textParts[1].options,
        bullet: false,
        numbered: false,
      };
    }

    if (textParts.length > 0) {
      textBlocks.push({ textParts, indentLevel });
    }
  }

  function processNode(
    node: MarkdownNode,
    indentLevel = 0,
    context: {
      isOrderedList?: boolean;
      itemIndex?: number;
      isBullet?: boolean;
    } = {}
  ): void {
    if (!node || typeof node !== 'object') return;

    switch (node.type) {
      case 'root':
        node.children?.forEach((child) => processNode(child, indentLevel));
        break;

      case 'heading': {
        const textParts = [
          {
            text:
              node.children
                ?.map((child) => {
                  if (child.type === 'text') return child.value || '';
                  if (child.type === 'strong') return processStrongNode(child);
                  if (child.type === 'emphasis') return processEmphasisNode(child);
                  if (child.type === 'link') return processLinkNode(child);
                  if (child.type === 'inlineCode') return processInlineCodeNode(child);
                  return '';
                })
                .join('') || '',
            options: { bold: true },
          },
        ];

        textBlocks.push({ textParts, indentLevel });
        break;
      }
      case 'paragraph':
        processParagraph(
          node,
          indentLevel,
          context.isBullet || false,
          context.isOrderedList || false,
          context.itemIndex
        );
        break;

      case 'list': {
        const isOrdered = node.ordered === true;

        node.children?.forEach((item, index) => {
          if (item.type === 'listItem') {
            item.children?.forEach((child) => {
              if (child.type === 'paragraph') {
                const useBullet = !isOrdered;
                processParagraph(
                  child,
                  indentLevel,
                  useBullet,
                  isOrdered,
                  isOrdered ? index + 1 : undefined
                );
              } else if (child.type === 'list') {
                processNode(child, indentLevel + 1);
              } else {
                processNode(child, indentLevel);
              }
            });
          }
        });
        break;
      }
      case 'listItem': {
        node.children?.forEach((child) => {
          if (child.type === 'paragraph') {
            const useBullet = context.isBullet !== undefined ? context.isBullet : true;
            processParagraph(
              child,
              indentLevel,
              useBullet,
              context.isOrderedList || false,
              context.itemIndex
            );
          } else if (child.type === 'list') {
            processNode(child, indentLevel + 1);
          } else {
            processNode(child, indentLevel);
          }
        });
        break;
      }
      case 'code': {
        const codeText = node.value || '';
        const textParts = [
          {
            text: codeText,
            options: {},
          },
        ];
        textBlocks.push({ textParts, indentLevel });
        break;
      }
      case 'blockquote': {
        node.children?.forEach((child) => {
          processNode(child, indentLevel + 1);
        });
        break;
      }
      case 'table': {
        node.children?.forEach((row) => {
          if (row.type === 'tableRow') {
            const rowTexts: string[] = [];
            row.children?.forEach((cell) => {
              if (cell.children && cell.children.length > 0) {
                const cellText = cell.children
                  .map((content) => {
                    if (content.type === 'text') return content.value || '';
                    if (content.type === 'strong') return processStrongNode(content);
                    return '';
                  })
                  .join('');
                rowTexts.push(cellText);
              }
            });

            if (rowTexts.length > 0) {
              const textParts = [
                {
                  text: rowTexts.join(' | '),
                  options: {},
                },
              ];
              textBlocks.push({ textParts, indentLevel });
            }
          }
        });
        break;
      }

      default:
        if (node.children) {
          node.children.forEach((child) => processNode(child, indentLevel));
        }
        break;
    }
  }

  processNode(tree as MarkdownNode);
  return textBlocks;
};

export const cleanMarkdownText = (text: string): string => {
  let cleanedText = text.replace(/\n{3,}/g, '\n\n');
  cleanedText = cleanedText.replace(/^(\s*)(•)(\s+)/gm, '$1-$3');

  return cleanedText;
};

export const cleanMarkdownTitle = (text: string): string => {
  return text
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/###/g, '')
    .replace(/#/g, '')
    .replace(/"/g, '')
    .trim();
};

const getAreaImpacts = (
  questions: ProjectRunAnalysisQuestion[],
  rubricCore: Record<string, Record<string, RubricCore[]>>,
  rubricType: string
): ImpactContent[] => {
  const areas = getUniqueAreas(questions);
  const areaImpacts: ImpactContent[] = [];
  const areaMap = groupByAreaAndDimension(questions);

  areas.forEach((area) => {
    const dimensionQuestions = Object.values(areaMap[area] || {}).map((questions) => questions[0]);

    const areaRubricCore = rubricCore[area];
    const dimensionValues = calculateDimensionValues(dimensionQuestions, areaRubricCore);
    const rating = calculateOverallRating(dimensionValues);

    const roundedRating = Math.round(rating);
    const maxRating = rubricType === 'LMH' ? 3 : Number(rubricType);
    const impact = getTileColor(roundedRating - 1, rating, maxRating);

    areaImpacts.push({
      area,
      impact,
    });
  });

  return areaImpacts;
};

export const parseToControlSlideData = async (
  titleMd: string,
  contentMd: string,
  rubricQuestions: ProjectRunAnalysisQuestion[],
  rubricCore: Record<string, Record<string, RubricCore[]>>,
  rubricType: string
): Promise<ControlSlideData> => {
  const textBlocks = parseMarkdownToTextBlocks(contentMd);
  return {
    title: cleanMarkdownTitle(titleMd),
    topics: textBlocks,
    areas: getAreaImpacts(rubricQuestions, rubricCore, rubricType),
  };
};
